import api from '../../../core/api/client';
import { projectService } from './projectsApi';
import { taskService } from './tasksApi';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.[key])) {
    return payload[key];
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const normalizeMilestone = (milestone) => {
  if (!milestone || typeof milestone !== 'object') {
    return milestone;
  }

  return {
    ...milestone,
    id: milestone.id,
    projectId: milestone.project_id || milestone.projectId || null,
    title: milestone.title,
    description: milestone.description,
    dueDate: milestone.due_date || milestone.dueDate || null,
    overseer: milestone.overseer,
    overseerName: milestone.overseer_name || milestone.overseerName || null,
    status: milestone.status,
    progress: milestone.progress,
    createdAt: milestone.created_at || milestone.createdAt || null,
    updatedAt: milestone.updated_at || milestone.updatedAt || null,
  };
};

const normalizeMilestoneList = (payload, key) => {
  const list = normalizeList(payload, key);
  return Array.isArray(list) ? list.map(normalizeMilestone) : [];
};

const serializeMilestonePayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const result = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) return;

    switch (key) {
      case 'dueDate':
        result.due_date = value;
        break;
      case 'overseerName':
        result.overseer_name = value;
        break;
      case 'projectId':
        result.project_id = value;
        break;
      case 'createdAt':
        result.created_at = value;
        break;
      case 'updatedAt':
        result.updated_at = value;
        break;
      default:
        result[key] = value;
    }
  });

  return result;
};

const normalizeTask = (task) => {
  if (!task || typeof task !== 'object') return task;
  return {
    ...task,
    milestoneId: task.milestone_id ?? task.milestoneId ?? null,
    projectId:   task.project_id   ?? task.projectId   ?? null,
    startDate:   task.start_date   ?? task.startDate   ?? null,
    dueDate:     task.due_date     ?? task.dueDate     ?? null,
    isDisposed:  task.is_disposed  ?? task.isDisposed  ?? false,
    createdAt:   task.created_at   ?? task.createdAt   ?? null,
    updatedAt:   task.updated_at   ?? task.updatedAt   ?? null,
    createdBy:   task.created_by   ?? task.createdBy   ?? null,
    assigneeIds: task.assignee_ids ?? task.assigneeIds ?? [],
  };
};

const serializeTaskPayload = (data) => {
  if (!data || typeof data !== 'object') return data;
  const camelToSnake = {
    milestoneId: 'milestone_id',
    projectId:   'project_id',
    startDate:   'start_date',
    dueDate:     'due_date',
    isDisposed:  'is_disposed',
    createdAt:   'created_at',
    updatedAt:   'updated_at',
    createdBy:   'created_by',
    assigneeIds: 'assignee_ids',
  };
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined) continue;
    out[camelToSnake[k] ?? k] = v;
  }
  return out;
};

const normalizeDocument = (d) => {
  if (!d || typeof d !== 'object') return d;
  return {
    ...d,
    id:          d.id,
    projectId:   d.project_id  ?? d.projectId  ?? null,
    name:        d.name,
    type:        d.type,
    description: d.description ?? null,
    url:         d.url         ?? null,
    fileUrl:     d.file_url    ?? d.fileUrl    ?? null,
    fileName:    d.file_name   ?? d.fileName   ?? null,
    fileType:    d.file_type   ?? d.fileType   ?? null,
    fileSize:    d.file_size   ?? d.fileSize   ?? null,
    filePath:    d.file_path   ?? d.filePath   ?? null,
    uploadedBy:  d.uploaded_by ?? d.uploadedBy ?? null,
    createdAt:   d.created_at  ?? d.createdAt  ?? null,
    updatedAt:   d.updated_at  ?? d.updatedAt  ?? null,
  };
};

const normalizeComment = (c) => {
  if (!c || typeof c !== 'object') return c;
  return {
    ...c,
    id:         c.id,
    projectId:  c.project_id  ?? c.projectId  ?? null,
    content:    c.content,
    authorId:   c.author_id   ?? c.authorId   ?? null,
    authorName: c.author_name ?? c.authorName ?? 'Unknown',
    likes:      Array.isArray(c.likes) ? c.likes : [],
    parentId:   c.parent_id   ?? c.parentId   ?? null,
    createdAt:  c.created_at  ?? c.createdAt  ?? null,
    updatedAt:  c.updated_at  ?? c.updatedAt  ?? null,
  };
};

const safeGetProject = async (projectId) => {
  try {
    return await projectService.getProject(projectId);
  } catch (error) {
    return null;
  }
};

const normalizeMilestoneId = (milestoneId) => {
  if (milestoneId === null || milestoneId === undefined) {
    throw new Error('milestone_id is required');
  }

  const normalized = String(milestoneId).trim();
  if (!normalized) {
    throw new Error('milestone_id is required');
  }

  return encodeURIComponent(normalized);
};

export const projectBoardService = {
  async getProject(projectId) {
    return projectService.getProject(projectId);
  },

  async getProjectTasks(projectId) {
    const payload = await taskService.getProjectTasks(projectId);
    return normalizeList(payload, 'tasks').map(normalizeTask);
  },

  async getUserTasks() {
    const payload = await taskService.getMyTasks();
    return normalizeList(payload, 'tasks').map(normalizeTask);
  },

  async getMilestoneTasks(projectId, milestoneId) {
    const payload = await taskService.getMilestoneTasks(projectId, milestoneId);
    return normalizeList(payload, 'tasks').map(normalizeTask);
  },

  async updateTask(_projectId, taskId, updates) {
    const result = await taskService.updateTask(taskId, serializeTaskPayload(updates));
    return normalizeTask(result);
  },

  async updateTaskStatus(_projectId, taskId, status) {
    const result = await taskService.updateTaskStatus(taskId, status);
    return result ? normalizeTask(result) : result;
  },

  async createTask(projectId, taskData) {
    const result = await taskService.createTask(projectId, serializeTaskPayload(taskData));
    return normalizeTask(result);
  },

  async deleteProjectTask(_projectId, taskId) {
    return taskService.deleteTask(taskId);
  },

  async getTaskEvidence(projectId, taskId) {
    const [tasks, documents] = await Promise.all([
      this.getProjectTasks(projectId),
      this.getProjectDocuments(projectId),
    ]);

    const task = tasks.find((item) => item.id === taskId);
    const evidenceIds = Array.isArray(task?.evidence) ? task.evidence : [];

    if (evidenceIds.length === 0) {
      return [];
    }

    return documents.filter((document) => evidenceIds.includes(document.id));
  },

  async getProjectMilestones(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/milestones`);
      return normalizeMilestoneList(response.data, 'milestones');
    } catch (error) {
      const project = await safeGetProject(projectId);
      return Array.isArray(project?.milestones) ? project.milestones.map(normalizeMilestone) : [];
    }
  },

  async createMilestone(projectId, milestoneData) {
    try {
      const response = await api.post(`/projects/${projectId}/milestones`, serializeMilestonePayload(milestoneData));
      return normalizeMilestone(response.data);
    } catch (error) {
      const project = await projectService.getProject(projectId);
      const milestones = Array.isArray(project?.milestones) ? [...project.milestones] : [];
      const createdMilestone = {
        ...milestoneData,
        id: `ms-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      milestones.push(createdMilestone);
      await projectService.patchProject(projectId, { milestones });
      return createdMilestone;
    }
  },

  async getMilestoneById(milestoneId) {
    try {
      const response = await api.get(`/milestones/${normalizeMilestoneId(milestoneId)}`);
      return normalizeMilestone(response.data);
    } catch (error) {
      console.error('Error fetching milestone by id:', error);
      return null;
    }
  },

  async updateMilestone(projectId, milestoneId, milestoneData) {
    try {
      const response = await api.patch(`/milestones/${normalizeMilestoneId(milestoneId)}`, serializeMilestonePayload(milestoneData));
      return normalizeMilestone(response.data);
    } catch (error) {
      const project = await projectService.getProject(projectId);
      const milestones = Array.isArray(project?.milestones)
        ? project.milestones.map((milestone) =>
            milestone.id === milestoneId
              ? { ...milestone, ...milestoneData, updatedAt: new Date().toISOString() }
              : milestone,
          )
        : [];
      await projectService.patchProject(projectId, { milestones });
      return normalizeMilestone(milestones.find((milestone) => milestone.id === milestoneId) || null);
    }
  },

  async deleteMilestone(projectId, milestoneId) {
    try {
      const response = await api.delete(`/milestones/${normalizeMilestoneId(milestoneId)}`);
      return response.data;
    } catch (error) {
      const project = await projectService.getProject(projectId);
      const milestones = Array.isArray(project?.milestones)
        ? project.milestones.filter((milestone) => milestone.id !== milestoneId)
        : [];
      await projectService.patchProject(projectId, { milestones });
      return true;
    }
  },

  async recomputeMilestoneProgress(projectId, milestoneId) {
    try {
      const response = await api.post(`/projects/${projectId}/milestones/${normalizeMilestoneId(milestoneId)}/recompute-progress`);
      return response.data;
    } catch (error) {
      console.error('Error recomputing milestone progress:', error);
      return null;
    }
  },

  async updateMilestoneProgress(projectId, milestoneId) {
    const tasks = await this.getMilestoneTasks(projectId, milestoneId);
    const nonTrashTasks = tasks.filter((task) => task.status !== 'trash');
    const completedTasks = nonTrashTasks.filter((task) => task.status === 'done').length;
    const progress = nonTrashTasks.length === 0 ? 0 : Math.round((completedTasks / nonTrashTasks.length) * 100);
    await this.updateMilestone(projectId, milestoneId, { progress });

    try {
      await projectService.recomputeProjectProgress(projectId);
    } catch (error) {
      // Keep milestone progress update successful even if project recompute endpoint is unavailable.
    }

    return progress;
  },

  async getProjectTeam(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/team`);
      return normalizeList(response.data, 'team');
    } catch (error) {
      const project = await safeGetProject(projectId);
      if (Array.isArray(project?.members) && project.members.length > 0) {
        return project.members;
      }

      return Array.isArray(project?.team) ? project.team : [];
    }
  },

  async getProjectDocuments(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/documents`);
      return normalizeList(response.data, 'documents').map(normalizeDocument);
    } catch {
      return [];
    }
  },

  // Upload a binary file — POST /projects/{id}/documents/upload (multipart)
  async uploadProjectFile(projectId, file, name, description) {
    const formData = new FormData();
    formData.append('file', file);
    if (name)        formData.append('name', name);
    if (description) formData.append('description', description);
    const response = await api.post(
      `/projects/${projectId}/documents/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return normalizeDocument(response.data);
  },

  // Attach an external link — POST /projects/{id}/documents/link
  async addProjectLink(projectId, linkData) {
    const response = await api.post(`/projects/${projectId}/documents/link`, {
      name:        linkData.name,
      type:        'link',
      description: linkData.description ?? '',
      url:         linkData.url,
    });
    return normalizeDocument(response.data);
  },

  // Keep legacy signature used by existing call-sites
  async addProjectDocument(projectId, documentData, file) {
    if (file) {
      return this.uploadProjectFile(
        projectId,
        file,
        documentData?.name,
        documentData?.description
      );
    }
    return this.addProjectLink(projectId, documentData);
  },

  async updateDocumentMetadata(documentId, updates) {
    const response = await api.patch(`/documents/${documentId}`, {
      ...(updates.name        !== undefined && { name: updates.name }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.url         !== undefined && { url: updates.url }),
    });
    return normalizeDocument(response.data);
  },

  async deleteProjectDocument(_projectId, documentId) {
    await api.delete(`/documents/${documentId}`);
    return true;
  },

  async getProjectActivities(projectId) {
    try {
      const res = await api.get(`/activities/projects/${projectId}`, { params: { limit: 100 } });
      const data = res.data;
      return Array.isArray(data) ? data : (data?.activities ?? []);
    } catch {
      return [];
    }
  },

  async addProjectActivity(projectId, activityData) {
    // endpoint not yet implemented — persist locally on the project document until available
    const project = await projectService.getProject(projectId);
    const activities = Array.isArray(project?.activities) ? [...project.activities] : [];
    const createdActivity = { ...activityData, id: `act-${Date.now()}` };
    activities.unshift(createdActivity);
    await projectService.patchProject(projectId, { activities });
    return createdActivity;
  },

  async updateProjectStatus(projectId, status) {
    return projectService.changeProjectStatus(projectId, status);
  },

  async getProjectComments(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/comments`);
      return normalizeList(response.data, 'comments').map(normalizeComment);
    } catch {
      return [];
    }
  },

  async addProjectComment(projectId, commentData) {
    const response = await api.post(`/projects/${projectId}/comments`, {
      content: commentData.content,
      ...(commentData.parentId || commentData.parent_id
        ? { parent_id: commentData.parentId ?? commentData.parent_id }
        : {}),
    });
    return normalizeComment(response.data);
  },

  async updateProjectComment(_projectId, commentId, updates) {
    const response = await api.patch(`/comments/${commentId}`, { content: updates.content });
    return normalizeComment(response.data);
  },

  async deleteProjectComment(_projectId, commentId) {
    await api.delete(`/comments/${commentId}`);
    return true;
  },

  async toggleCommentLike(_projectId, commentId) {
    const response = await api.post(`/comments/${commentId}/like`);
    return normalizeComment(response.data);
  },
};
